/**
 * ADD-footer primary CTA for the family day view (`/orders/family/day`) —
 * the fill-the-week funnel: "Next day" targets the first LATER weekday that
 * still needs items, "Back to {Weekday}" wraps to the earliest unhandled one
 * once nothing later needs any, and "Review & Checkout" appears only when no
 * OTHER day of the week needs items. The viewed day is the parent's own call
 * — it never blocks checkout.
 *
 * A day "needs items" only while it's actionable: closed days, days already
 * carrying cart lines, and days with a placed order are handled. A day whose
 * menus haven't loaded yet counts as needing — never claim the week is done
 * early (same convention as `lib/school-orders/day-cta.ts`'s
 * `hasMenu: null`). Pure — the day view composes the query state.
 */

import { type ChildBandContext, orderableItemsByMealtime } from './band-menus'

export type WeekCta =
  | { kind: 'next-day'; targetDate: string }
  | { kind: 'back-to'; targetDate: string }
  | { kind: 'checkout' }

export function resolveWeekCta(args: {
  /** `weekOf(date)` — the week's Mon–Fri keys, ascending. */
  weekKeys: readonly string[]
  /**
   * The viewed day, `YYYY-MM-DD`. Compared lexicographically against the
   * week keys, so a weekend deep link (not in `weekKeys`) still resolves.
   */
  currentDate: string
  needsItems: (dayKey: string) => boolean
  /**
   * Which LATER days "Next day" may target — defaults to `needsItems` (skip
   * already-handled days). Passing an "enterable" predicate (open +
   * fillable, ignoring cart/orders) flips to the literal-next-weekday
   * variant; back-to and checkout keep `needsItems` either way.
   */
  forwardEligible?: (dayKey: string) => boolean
}): WeekCta {
  const forwardOk = args.forwardEligible ?? args.needsItems
  const forward = args.weekKeys.find((k) => k > args.currentDate && forwardOk(k))
  if (forward) {
    return { kind: 'next-day', targetDate: forward }
  }
  const backward = args.weekKeys.find((k) => k < args.currentDate && args.needsItems(k))
  if (backward) {
    return { kind: 'back-to', targetDate: backward }
  }
  return { kind: 'checkout' }
}

export interface DayNeedsItemsInput {
  /** Any cart line dated this day — across every campus (`useAllCartLines`). */
  hasCartLine: boolean
  /**
   * Any non-VOIDED batch dated this day, at any campus — auto-orders and
   * payment-pending DRAFTs included (routing into one shows the receipt /
   * pending banner, not steppers, so the cycle must treat it as handled).
   */
  hasPlacedOrder: boolean
  /** `isDayClosed(activeLocation, dayKey)` — past, or its deadline passed. */
  closed: boolean
  /** `dayFillable(...)` — null while the day's menus query is pending/errored. */
  fillable: boolean | null
}

/** Unknown fillability counts as needing — never claim the week is done early. */
export function dayNeedsItems({ hasCartLine, hasPlacedOrder, closed, fillable }: DayNeedsItemsInput): boolean {
  if (closed || hasCartLine || hasPlacedOrder) {
    return false
  }
  return fillable !== false
}

/**
 * Structural subset of `LocationActiveMenusResponse` (`@/api/orders`) —
 * keeps this module react-query-free; query data assigns directly.
 */
export interface FillableDayData {
  menusForLocation: ReadonlyArray<{
    isArchived: boolean
    blockedOnDate?: boolean
    gradeRangeCodes?: string[] | null
    mealtime: string
    items: ReadonlyArray<{ visible: boolean; isArchived: boolean; sortOrder: number }>
  }>
  dayContext?: { noServiceDay: { notes: string | null } | null } | null
}

/**
 * A day is fillable when it isn't a term-calendar no-service day and at
 * least one of the campus's children has an orderable (band-gated) menu with
 * visible items. `null` = menus not loaded yet (pending/errored query).
 */
export function dayFillable(
  data: FillableDayData | null | undefined,
  children: readonly ChildBandContext[],
): boolean | null {
  if (!data) {
    return null
  }
  if (data.dayContext?.noServiceDay) {
    return false
  }
  return children.some((child) => orderableItemsByMealtime(data.menusForLocation, child).size > 0)
}
